/**
 * Biometric Fatigue Monitoring Page
 * Integrates with wearable devices for crew fatigue tracking
 */
import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Slider } from '@/components/ui/slider';
import { Progress } from '@/components/ui/progress';
import { Textarea } from '@/components/ui/textarea';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import {
  Activity, Heart, Moon, Sun, Brain, Clock, TrendingUp, TrendingDown,
  AlertTriangle, CheckCircle, Watch, Smartphone, Plus, RefreshCw,
  Wifi, WifiOff, BarChart3, Zap, Coffee, Wine, FileText
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Fatigue level colors
const FATIGUE_COLORS = {
  low: 'bg-green-500',
  moderate: 'bg-yellow-500',
  high: 'bg-orange-500',
  severe: 'bg-red-500'
};

const FATIGUE_LABELS = {
  low: 'Well Rested',
  moderate: 'Moderate',
  high: 'Elevated',
  severe: 'Critical'
};

// Wearable providers
const WEARABLE_PROVIDERS = [
  { id: 'fitbit', name: 'Fitbit', icon: Watch, color: 'bg-cyan-500' },
  { id: 'apple_health', name: 'Apple Health', icon: Smartphone, color: 'bg-gray-800' },
  { id: 'google_fit', name: 'Google Fit', icon: Activity, color: 'bg-blue-500' },
  { id: 'garmin', name: 'Garmin', icon: Watch, color: 'bg-blue-700' }
];

export default function BiometricMonitoringPage() {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState('dashboard');
  const [dashboard, setDashboard] = useState(null);
  const [entries, setEntries] = useState([]);
  const [wearables, setWearables] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showEntryModal, setShowEntryModal] = useState(false);
  const [showAssessmentModal, setShowAssessmentModal] = useState(false);
  const [assessmentResult, setAssessmentResult] = useState(null);

  // Entry form state
  const [entryForm, setEntryForm] = useState({
    date: new Date().toISOString().split('T')[0],
    source: 'manual',
    sleep_hours: '',
    sleep_quality: '',
    resting_heart_rate: '',
    heart_rate_variability: '',
    perceived_fatigue: 5,
    alertness_level: 5,
    stress_level: 50,
    recovery_score: 50,
    caffeine_intake: '',
    alcohol_units: '',
    notes: ''
  });

  // Assessment form state
  const [assessmentForm, setAssessmentForm] = useState({
    duty_start: '',
    duty_end: '',
    flight_sectors: 1,
    time_zone_crossings: 0
  });

  const token = localStorage.getItem('token');

  useEffect(() => {
    fetchDashboard();
    fetchEntries();
    fetchWearables();
  }, []);

  const fetchDashboard = async () => {
    try {
      const res = await fetch(`${API_URL}/api/biometric/dashboard`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setDashboard(data);
      }
    } catch (error) {
      console.error('Failed to fetch dashboard:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchEntries = async () => {
    try {
      const res = await fetch(`${API_URL}/api/biometric/entries?limit=30`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setEntries(data);
      }
    } catch (error) {
      console.error('Failed to fetch entries:', error);
    }
  };

  const fetchWearables = async () => {
    try {
      const res = await fetch(`${API_URL}/api/biometric/wearables`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setWearables(data);
      }
    } catch (error) {
      console.error('Failed to fetch wearables:', error);
    }
  };

  const handleCreateEntry = async () => {
    try {
      const payload = {
        ...entryForm,
        sleep_hours: entryForm.sleep_hours ? parseFloat(entryForm.sleep_hours) : null,
        resting_heart_rate: entryForm.resting_heart_rate ? parseInt(entryForm.resting_heart_rate) : null,
        heart_rate_variability: entryForm.heart_rate_variability ? parseFloat(entryForm.heart_rate_variability) : null,
        caffeine_intake: entryForm.caffeine_intake ? parseInt(entryForm.caffeine_intake) : null,
        alcohol_units: entryForm.alcohol_units ? parseFloat(entryForm.alcohol_units) : null,
        perceived_fatigue: entryForm.perceived_fatigue,
        alertness_level: entryForm.alertness_level,
        stress_level: entryForm.stress_level,
        recovery_score: entryForm.recovery_score,
        sleep_quality: entryForm.sleep_quality || null
      };

      const res = await fetch(`${API_URL}/api/biometric/entries`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      if (res.ok) {
        toast.success('Biometric entry recorded');
        setShowEntryModal(false);
        fetchDashboard();
        fetchEntries();
        // Reset form
        setEntryForm({
          date: new Date().toISOString().split('T')[0],
          source: 'manual',
          sleep_hours: '',
          sleep_quality: '',
          resting_heart_rate: '',
          heart_rate_variability: '',
          perceived_fatigue: 5,
          alertness_level: 5,
          stress_level: 50,
          recovery_score: 50,
          caffeine_intake: '',
          alcohol_units: '',
          notes: ''
        });
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to create entry');
      }
    } catch (error) {
      toast.error('Failed to create entry');
    }
  };

  const handleAssessment = async () => {
    try {
      const params = new URLSearchParams({
        duty_start: assessmentForm.duty_start,
        duty_end: assessmentForm.duty_end,
        flight_sectors: assessmentForm.flight_sectors,
        time_zone_crossings: assessmentForm.time_zone_crossings
      });

      const res = await fetch(`${API_URL}/api/biometric/assess/self?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (res.ok) {
        const data = await res.json();
        setAssessmentResult(data);
        toast.success('Assessment complete');
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Assessment failed');
      }
    } catch (error) {
      toast.error('Assessment failed');
    }
  };

  const handleConnectWearable = async (providerId) => {
    try {
      // Get OAuth authorization URL from backend
      const res = await fetch(`${API_URL}/api/biometric/wearables/oauth/${providerId}/authorize`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.ok) {
        const data = await res.json();
        if (data.authorize_url) {
          // Redirect to OAuth provider
          window.location.href = data.authorize_url;
        } else {
          toast.error('OAuth not configured. Contact your administrator.');
        }
      } else {
        const err = await res.json();
        toast.info(err.detail || `Wearable integration requires OAuth setup. Contact your administrator.`);
      }
    } catch (error) {
      toast.error('Failed to initiate wearable connection');
    }
  };

  const handleSyncWearable = async (providerId) => {
    try {
      const res = await fetch(`${API_URL}/api/biometric/wearables/${providerId}/sync`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.ok) {
        const data = await res.json();
        toast.success(data.message || 'Sync initiated');
        fetchWearables();
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Sync failed');
      }
    } catch (error) {
      toast.error('Failed to sync wearable data');
    }
  };

  const getFatigueScoreColor = (score) => {
    if (score >= 70) return 'text-green-500';
    if (score >= 50) return 'text-yellow-500';
    if (score >= 30) return 'text-orange-500';
    return 'text-red-500';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="h-8 w-8 animate-spin text-sky-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="biometric-monitoring-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
            <Heart className="h-6 w-6 text-red-500" />
            {t('biometric.title')}
          </h1>
          <p className="text-gray-500 dark:text-gray-400 mt-1">
            {t('biometric.subtitle')}
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setShowAssessmentModal(true)}>
            <Brain className="h-4 w-4 mr-2" />
            {t('biometric.fatigue')}
          </Button>
          <Button onClick={() => setShowEntryModal(true)} data-testid="add-entry-btn">
            <Plus className="h-4 w-4 mr-2" />
            {t('common.add')}
          </Button>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-4 max-w-2xl">
          <TabsTrigger value="dashboard" data-testid="tab-dashboard">
            <BarChart3 className="h-4 w-4 mr-2" />
            {t('common.dashboard')}
          </TabsTrigger>
          <TabsTrigger value="history" data-testid="tab-history">
            <Clock className="h-4 w-4 mr-2" />
            {t('common.history')}
          </TabsTrigger>
          <TabsTrigger value="wearables" data-testid="tab-wearables">
            <Watch className="h-4 w-4 mr-2" />
            Wearables
          </TabsTrigger>
          <TabsTrigger value="insights" data-testid="tab-insights">
            <Brain className="h-4 w-4 mr-2" />
            Insights
          </TabsTrigger>
        </TabsList>

        {/* Dashboard Tab */}
        <TabsContent value="dashboard" className="space-y-6">
          {/* Quick Stats */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-500">Fatigue Score</p>
                    <p className={`text-3xl font-bold ${getFatigueScoreColor(dashboard?.statistics?.avg_fatigue_score || 0)}`}>
                      {dashboard?.statistics?.avg_fatigue_score || '--'}
                    </p>
                  </div>
                  <Activity className={`h-10 w-10 ${getFatigueScoreColor(dashboard?.statistics?.avg_fatigue_score || 0)}`} />
                </div>
                <p className="text-xs text-gray-400 mt-2">30-day average</p>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-500">Avg Sleep</p>
                    <p className="text-3xl font-bold text-blue-500">
                      {dashboard?.statistics?.avg_sleep_hours?.toFixed(1) || '--'}h
                    </p>
                  </div>
                  <Moon className="h-10 w-10 text-blue-500" />
                </div>
                <p className="text-xs text-gray-400 mt-2">Target: 7-9h</p>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-500">Avg HRV</p>
                    <p className="text-3xl font-bold text-purple-500">
                      {dashboard?.statistics?.avg_hrv?.toFixed(0) || '--'}ms
                    </p>
                  </div>
                  <Heart className="h-10 w-10 text-purple-500" />
                </div>
                <p className="text-xs text-gray-400 mt-2">Higher is better</p>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-500">Trend</p>
                    <p className="text-3xl font-bold capitalize">
                      {dashboard?.statistics?.trend || '--'}
                    </p>
                  </div>
                  {dashboard?.statistics?.trend === 'improving' ? (
                    <TrendingUp className="h-10 w-10 text-green-500" />
                  ) : dashboard?.statistics?.trend === 'declining' ? (
                    <TrendingDown className="h-10 w-10 text-red-500" />
                  ) : (
                    <Activity className="h-10 w-10 text-gray-400" />
                  )}
                </div>
                <p className="text-xs text-gray-400 mt-2">7-day comparison</p>
              </CardContent>
            </Card>
          </div>

          {/* Chart Area */}
          <Card>
            <CardHeader>
              <CardTitle>Fatigue Trend</CardTitle>
              <CardDescription>Your fatigue score over the last 30 days</CardDescription>
            </CardHeader>
            <CardContent>
              {dashboard?.chart_data?.length > 0 ? (
                <div className="h-64 flex items-end gap-1">
                  {dashboard.chart_data.slice(-30).map((point, i) => (
                    <div
                      key={i}
                      className="flex-1 bg-sky-500 rounded-t hover:bg-sky-600 transition-colors"
                      style={{ height: `${(point.fatigue_score || 0)}%` }}
                      title={`${point.date}: ${point.fatigue_score || 0}`}
                    />
                  ))}
                </div>
              ) : (
                <div className="h-64 flex items-center justify-center text-gray-500">
                  <div className="text-center">
                    <BarChart3 className="h-12 w-12 mx-auto mb-2 opacity-50" />
                    <p>No data yet. Start logging your biometrics!</p>
                    <Button variant="outline" className="mt-4" onClick={() => setShowEntryModal(true)}>
                      <Plus className="h-4 w-4 mr-2" /> Add First Entry
                    </Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Latest Entry */}
          {dashboard?.latest_entry && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FileText className="h-5 w-5" />
                  Latest Entry
                </CardTitle>
                <CardDescription>{dashboard.latest_entry.date}</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div>
                    <p className="text-sm text-gray-500">Sleep</p>
                    <p className="font-semibold">{dashboard.latest_entry.sleep_hours || '--'}h</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Fatigue Score</p>
                    <p className={`font-semibold ${getFatigueScoreColor(dashboard.latest_entry.fatigue_score)}`}>
                      {dashboard.latest_entry.fatigue_score || '--'}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">HRV</p>
                    <p className="font-semibold">{dashboard.latest_entry.heart_rate_variability || '--'}ms</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Level</p>
                    <Badge className={FATIGUE_COLORS[dashboard.latest_entry.fatigue_level] || 'bg-gray-500'}>
                      {FATIGUE_LABELS[dashboard.latest_entry.fatigue_level] || '--'}
                    </Badge>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* History Tab */}
        <TabsContent value="history">
          <Card>
            <CardHeader>
              <CardTitle>Biometric History</CardTitle>
              <CardDescription>Your recorded biometric data entries</CardDescription>
            </CardHeader>
            <CardContent>
              <ScrollArea className="h-[500px]">
                {entries.length > 0 ? (
                  <div className="space-y-3">
                    {entries.map((entry) => (
                      <div
                        key={entry.id}
                        className="p-4 border rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
                      >
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-2">
                            <Badge variant="outline">{entry.date}</Badge>
                            <Badge className="capitalize">{entry.source}</Badge>
                          </div>
                          <Badge className={FATIGUE_COLORS[entry.fatigue_level] || 'bg-gray-500'}>
                            Score: {entry.fatigue_score}
                          </Badge>
                        </div>
                        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 text-sm">
                          <div>
                            <span className="text-gray-500">Sleep:</span>{' '}
                            <span className="font-medium">{entry.sleep_hours || '--'}h</span>
                          </div>
                          <div>
                            <span className="text-gray-500">HRV:</span>{' '}
                            <span className="font-medium">{entry.heart_rate_variability || '--'}ms</span>
                          </div>
                          <div>
                            <span className="text-gray-500">RHR:</span>{' '}
                            <span className="font-medium">{entry.resting_heart_rate || '--'}bpm</span>
                          </div>
                          <div>
                            <span className="text-gray-500">Alertness:</span>{' '}
                            <span className="font-medium">{entry.alertness_level || '--'}/10</span>
                          </div>
                          <div>
                            <span className="text-gray-500">Recovery:</span>{' '}
                            <span className="font-medium">{entry.recovery_score || '--'}%</span>
                          </div>
                        </div>
                        {entry.notes && (
                          <p className="text-sm text-gray-500 mt-2 italic">{entry.notes}</p>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-12 text-gray-500">
                    <Clock className="h-12 w-12 mx-auto mb-2 opacity-50" />
                    <p>No entries recorded yet</p>
                  </div>
                )}
              </ScrollArea>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Wearables Tab */}
        <TabsContent value="wearables">
          <Card>
            <CardHeader>
              <CardTitle>Connected Devices</CardTitle>
              <CardDescription>Connect your wearable devices to automatically sync biometric data</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {WEARABLE_PROVIDERS.map((provider) => {
                  const connection = wearables.find(w => w.provider === provider.id);
                  const isConnected = connection?.connected;
                  const Icon = provider.icon;
                  
                  return (
                    <Card key={provider.id} className={isConnected ? 'border-green-500' : ''}>
                      <CardContent className="pt-6">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <div className={`p-3 rounded-lg ${provider.color}`}>
                              <Icon className="h-6 w-6 text-white" />
                            </div>
                            <div>
                              <p className="font-semibold">{provider.name}</p>
                              <p className="text-sm text-gray-500">
                                {isConnected ? 'Connected' : 'Not connected'}
                              </p>
                              {isConnected && connection?.last_sync && (
                                <p className="text-xs text-gray-400">
                                  Last sync: {new Date(connection.last_sync).toLocaleDateString()}
                                </p>
                              )}
                            </div>
                          </div>
                          {isConnected ? (
                            <div className="flex items-center gap-2">
                              <Button 
                                variant="outline" 
                                size="sm"
                                onClick={() => handleSyncWearable(provider.id)}
                                data-testid={`sync-${provider.id}-btn`}
                              >
                                <RefreshCw className="h-4 w-4 mr-1" />
                                Sync
                              </Button>
                              <Button variant="outline" size="sm" className="text-red-500 border-red-500 hover:bg-red-50">
                                Disconnect
                              </Button>
                            </div>
                          ) : (
                            <Button 
                              onClick={() => handleConnectWearable(provider.id)}
                              data-testid={`connect-${provider.id}-btn`}
                            >
                              Connect
                            </Button>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>

              <div className="mt-6 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                <h4 className="font-semibold text-blue-700 dark:text-blue-300 flex items-center gap-2">
                  <AlertTriangle className="h-4 w-4" />
                  Note on Wearable Integration
                </h4>
                <p className="text-sm text-blue-600 dark:text-blue-400 mt-1">
                  Wearable device connections require OAuth setup with each provider. Contact your system administrator
                  to configure the integration. In the meantime, you can manually log your biometric data.
                </p>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Insights Tab */}
        <TabsContent value="insights">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Brain className="h-5 w-5 text-purple-500" />
                  Fatigue Risk Factors
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <span className="text-sm">Sleep Debt</span>
                    <Badge variant="outline" className={
                      dashboard?.statistics?.avg_sleep_hours < 6 ? 'bg-red-100 text-red-700' :
                      dashboard?.statistics?.avg_sleep_hours < 7 ? 'bg-yellow-100 text-yellow-700' :
                      'bg-green-100 text-green-700'
                    }>
                      {dashboard?.statistics?.avg_sleep_hours < 6 ? 'High Risk' :
                       dashboard?.statistics?.avg_sleep_hours < 7 ? 'Moderate' : 'Low Risk'}
                    </Badge>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm">Recovery Status</span>
                    <Badge variant="outline" className="bg-blue-100 text-blue-700">
                      Monitor
                    </Badge>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm">Circadian Alignment</span>
                    <Badge variant="outline" className="bg-green-100 text-green-700">
                      Normal
                    </Badge>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <CheckCircle className="h-5 w-5 text-green-500" />
                  Recommendations
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <div className="flex items-start gap-2">
                    <Moon className="h-4 w-4 text-blue-500 mt-0.5" />
                    <p className="text-sm">Aim for 7-9 hours of sleep per night</p>
                  </div>
                  <div className="flex items-start gap-2">
                    <Coffee className="h-4 w-4 text-amber-600 mt-0.5" />
                    <p className="text-sm">Limit caffeine intake after 2pm</p>
                  </div>
                  <div className="flex items-start gap-2">
                    <Zap className="h-4 w-4 text-yellow-500 mt-0.5" />
                    <p className="text-sm">Take strategic naps before long duties</p>
                  </div>
                  <div className="flex items-start gap-2">
                    <Activity className="h-4 w-4 text-green-500 mt-0.5" />
                    <p className="text-sm">Regular exercise improves sleep quality</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>

      {/* Add Entry Modal */}
      <Dialog open={showEntryModal} onOpenChange={setShowEntryModal}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Log Biometric Data</DialogTitle>
            <DialogDescription>
              Record your daily biometric measurements
            </DialogDescription>
          </DialogHeader>

          <div className="grid grid-cols-2 gap-4 py-4">
            <div>
              <Label>Date</Label>
              <Input
                type="date"
                value={entryForm.date}
                onChange={(e) => setEntryForm({ ...entryForm, date: e.target.value })}
              />
            </div>
            <div>
              <Label>Data Source</Label>
              <Select
                value={entryForm.source}
                onValueChange={(v) => setEntryForm({ ...entryForm, source: v })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="manual">Manual Entry</SelectItem>
                  <SelectItem value="fitbit">Fitbit</SelectItem>
                  <SelectItem value="apple_health">Apple Health</SelectItem>
                  <SelectItem value="google_fit">Google Fit</SelectItem>
                  <SelectItem value="garmin">Garmin</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Sleep Duration (hours)</Label>
              <Input
                type="number"
                step="0.5"
                placeholder="7.5"
                value={entryForm.sleep_hours}
                onChange={(e) => setEntryForm({ ...entryForm, sleep_hours: e.target.value })}
              />
            </div>
            <div>
              <Label>Sleep Quality</Label>
              <Select
                value={entryForm.sleep_quality}
                onValueChange={(v) => setEntryForm({ ...entryForm, sleep_quality: v })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select quality" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="poor">Poor</SelectItem>
                  <SelectItem value="fair">Fair</SelectItem>
                  <SelectItem value="good">Good</SelectItem>
                  <SelectItem value="excellent">Excellent</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Resting Heart Rate (bpm)</Label>
              <Input
                type="number"
                placeholder="65"
                value={entryForm.resting_heart_rate}
                onChange={(e) => setEntryForm({ ...entryForm, resting_heart_rate: e.target.value })}
              />
            </div>
            <div>
              <Label>Heart Rate Variability (ms)</Label>
              <Input
                type="number"
                placeholder="45"
                value={entryForm.heart_rate_variability}
                onChange={(e) => setEntryForm({ ...entryForm, heart_rate_variability: e.target.value })}
              />
            </div>

            <div className="col-span-2">
              <Label>Perceived Fatigue (1=Alert, 10=Exhausted): {entryForm.perceived_fatigue}</Label>
              <Slider
                value={[entryForm.perceived_fatigue]}
                onValueChange={([v]) => setEntryForm({ ...entryForm, perceived_fatigue: v })}
                min={1}
                max={10}
                step={1}
                className="mt-2"
              />
            </div>

            <div className="col-span-2">
              <Label>Alertness Level (1=Drowsy, 10=Very Alert): {entryForm.alertness_level}</Label>
              <Slider
                value={[entryForm.alertness_level]}
                onValueChange={([v]) => setEntryForm({ ...entryForm, alertness_level: v })}
                min={1}
                max={10}
                step={1}
                className="mt-2"
              />
            </div>

            <div>
              <Label>Stress Level (%): {entryForm.stress_level}</Label>
              <Slider
                value={[entryForm.stress_level]}
                onValueChange={([v]) => setEntryForm({ ...entryForm, stress_level: v })}
                min={0}
                max={100}
                step={5}
                className="mt-2"
              />
            </div>

            <div>
              <Label>Recovery Score (%): {entryForm.recovery_score}</Label>
              <Slider
                value={[entryForm.recovery_score]}
                onValueChange={([v]) => setEntryForm({ ...entryForm, recovery_score: v })}
                min={0}
                max={100}
                step={5}
                className="mt-2"
              />
            </div>

            <div>
              <Label>Caffeine Intake (mg)</Label>
              <Input
                type="number"
                placeholder="200"
                value={entryForm.caffeine_intake}
                onChange={(e) => setEntryForm({ ...entryForm, caffeine_intake: e.target.value })}
              />
            </div>
            <div>
              <Label>Alcohol (units)</Label>
              <Input
                type="number"
                step="0.5"
                placeholder="0"
                value={entryForm.alcohol_units}
                onChange={(e) => setEntryForm({ ...entryForm, alcohol_units: e.target.value })}
              />
            </div>

            <div className="col-span-2">
              <Label>Notes</Label>
              <Textarea
                placeholder="Any relevant notes about your day..."
                value={entryForm.notes}
                onChange={(e) => setEntryForm({ ...entryForm, notes: e.target.value })}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEntryModal(false)}>Cancel</Button>
            <Button onClick={handleCreateEntry} data-testid="save-entry-btn">Save Entry</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Fatigue Assessment Modal */}
      <Dialog open={showAssessmentModal} onOpenChange={setShowAssessmentModal}>
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <DialogTitle>Pre-Duty Fatigue Assessment</DialogTitle>
            <DialogDescription>
              Assess your fatigue risk before an upcoming duty period
            </DialogDescription>
          </DialogHeader>

          {!assessmentResult ? (
            <>
              <div className="grid grid-cols-2 gap-4 py-4">
                <div>
                  <Label>Duty Start</Label>
                  <Input
                    type="datetime-local"
                    value={assessmentForm.duty_start}
                    onChange={(e) => setAssessmentForm({ ...assessmentForm, duty_start: e.target.value })}
                  />
                </div>
                <div>
                  <Label>Duty End</Label>
                  <Input
                    type="datetime-local"
                    value={assessmentForm.duty_end}
                    onChange={(e) => setAssessmentForm({ ...assessmentForm, duty_end: e.target.value })}
                  />
                </div>
                <div>
                  <Label>Flight Sectors</Label>
                  <Input
                    type="number"
                    min="1"
                    value={assessmentForm.flight_sectors}
                    onChange={(e) => setAssessmentForm({ ...assessmentForm, flight_sectors: parseInt(e.target.value) })}
                  />
                </div>
                <div>
                  <Label>Time Zones Crossed</Label>
                  <Input
                    type="number"
                    min="0"
                    value={assessmentForm.time_zone_crossings}
                    onChange={(e) => setAssessmentForm({ ...assessmentForm, time_zone_crossings: parseInt(e.target.value) })}
                  />
                </div>
              </div>

              <DialogFooter>
                <Button variant="outline" onClick={() => setShowAssessmentModal(false)}>Cancel</Button>
                <Button onClick={handleAssessment} disabled={!assessmentForm.duty_start || !assessmentForm.duty_end}>
                  Run Assessment
                </Button>
              </DialogFooter>
            </>
          ) : (
            <div className="py-4 space-y-4">
              {/* Assessment Results */}
              <div className="text-center">
                <div className={`inline-block p-4 rounded-full ${
                  assessmentResult.risk_level === 'low' ? 'bg-green-100' :
                  assessmentResult.risk_level === 'moderate' ? 'bg-yellow-100' :
                  assessmentResult.risk_level === 'high' ? 'bg-orange-100' : 'bg-red-100'
                }`}>
                  <p className={`text-4xl font-bold ${
                    assessmentResult.risk_level === 'low' ? 'text-green-600' :
                    assessmentResult.risk_level === 'moderate' ? 'text-yellow-600' :
                    assessmentResult.risk_level === 'high' ? 'text-orange-600' : 'text-red-600'
                  }`}>
                    {assessmentResult.fatigue_score}
                  </p>
                </div>
                <Badge className={`mt-2 ${FATIGUE_COLORS[assessmentResult.risk_level] || 'bg-gray-500'}`}>
                  {assessmentResult.risk_level?.toUpperCase()} RISK
                </Badge>
              </div>

              <div className="p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
                <p className="font-semibold">Recommendation</p>
                <p className="text-sm text-gray-600 dark:text-gray-300">{assessmentResult.recommendation}</p>
              </div>

              {assessmentResult.risk_factors?.length > 0 && (
                <div>
                  <p className="font-semibold mb-2">Risk Factors</p>
                  <div className="space-y-2">
                    {assessmentResult.risk_factors.map((rf, i) => (
                      <div key={i} className="flex items-center gap-2">
                        <AlertTriangle className={`h-4 w-4 ${
                          rf.severity === 'high' ? 'text-red-500' :
                          rf.severity === 'medium' ? 'text-yellow-500' : 'text-blue-500'
                        }`} />
                        <span className="text-sm">{rf.factor}: {rf.description}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {assessmentResult.ai_analysis && (
                <div className="p-4 bg-purple-50 dark:bg-purple-900/20 rounded-lg">
                  <p className="font-semibold flex items-center gap-2">
                    <Brain className="h-4 w-4 text-purple-500" />
                    AI Analysis
                  </p>
                  <p className="text-sm text-gray-600 dark:text-gray-300 mt-1">{assessmentResult.ai_analysis}</p>
                </div>
              )}

              <DialogFooter>
                <Button onClick={() => { setAssessmentResult(null); setShowAssessmentModal(false); }}>
                  Close
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
